import $axios from './apiConnection'
import parseCatch from '@/helpers/handleErrors.js'
import cloneDeep from 'lodash/cloneDeep'
import { IDashboard, IDashboardCopyRequest, IDashboardCreate, IDashboardRoot } from '@/types/dashboard'
import { GmtOffsetTypes } from '@/enum/gmt'

const initialTime = (new Date().getTime() / 1000).toFixed(0)

function timeout (ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

export const getAllDashboards = async () => {
    const { DashBoards = [] } = await $axios.get('/DashBoards/GetAll/') as { DashBoards: Array<IDashboard> }

    return DashBoards
}

export const DashboardApi = {
    async getAll (): Promise<Array<IDashboard>> {
        try {
            const DashBoards = await getAllDashboards()
            return DashBoards || []
        } catch (e) {
            try {
                parseCatch(e, true)
                const currentTime = (new Date().getTime() / 1000).toFixed(0)

                if (+currentTime - +initialTime < 11) {
                    await timeout(2000)
                    return await this.getAll()
                }
                return []
            } catch (e) {
                console.log('[DASHBOARD ERROR!!!!!!!]', e)
                // window.location.href = env.VITE_APP_FALLBACK_URL
                return []
            }

        }
    },

    async find (id: number) {
        try {
            const { Data } = await $axios.get(`/DashBoards/Get/${id}`) as { Data: IDashboardRoot }
            return Data.Dashboard
        } catch (e) {
            parseCatch(e, true, 'Find Dashboard')
        }
    },

    async update (data: Partial<IDashboard>) {
        try {
            const copyOfData = cloneDeep(data)
            const widgetGroupList = copyOfData.WidgetGroupList || []

            if (copyOfData.WidgetGroupList && copyOfData.WidgetGroupList.length) {
                delete copyOfData.WidgetGroupList
            }

            const dashboardData = await $axios.post('/DashBoards/Update/', copyOfData) as IDashboard
            dashboardData.WidgetGroupList = widgetGroupList

            return dashboardData
        } catch (e) {
            parseCatch(e, true, 'Update Dashboard')
        }
    },

    async store (data: Partial<IDashboardCreate>) {
        try {
            const { Data } = await $axios.post('/DashBoards/Add/', data) as { Data: IDashboard }
            return Data
        } catch (e) {
            parseCatch(e, true, 'Add Dashboard')
        }
    },

    async addWidgetGroup (dashboardID: number, widgetGroupID: number) {
        try {
            return await $axios.post(`/DashBoards/AddWidgetGroup/${dashboardID}/${widgetGroupID}`)
        } catch (e) {
            parseCatch(e, true, 'Add widget group to dashboard')
        }
    },

    async removeWidgetGroup (dashboardID: number, widgetGroupID: number) {
        try {
            return await $axios.post(`/DashBoards/RemoveWidgetGroup/${dashboardID}/${widgetGroupID}`)
        } catch (e) {
            parseCatch(e, true, 'Remove widget group from dashboard')
        }
    },

    async destroy (dashboardID: number) {
        try {
            return await $axios.post(`/DashBoards/RemoveDashboard/${dashboardID}`)
        } catch (e) {
            parseCatch(e, true)
        }
    },

    async copy (data: IDashboardCopyRequest) {
        try {
            return await $axios.post('/DashBoards/Copy/', data) as IDashboard
        } catch (e) {
            parseCatch(e, true)
        }
    },

    /**
     * Get an array of existing dashboard IDs
     */
    async getAccountDashboardList () {
        let res: Array<number> = []

        try {
            const { Data } = await $axios.get('/DashBoards/GetAccountDashboardList/') as { Data: Array<number> }

            res = Data
        } catch (e) {
            parseCatch(e, true)
        }

        return res
    },

    /**
     * Normalize GMTOffsetData.Type to ensure it's always an integer
     * Handles legacy data where Type might be stored as a string
     */
    normalizeGMTOffsetType (dashboard: Partial<IDashboard>): Partial<IDashboard> {
        if (dashboard.DashboardConf?.GMTOffsetData?.Type) {
            const type = dashboard.DashboardConf.GMTOffsetData.Type
            
            // If Type is already a number, leave it as is
            if (typeof type === 'number') {
                return dashboard
            }
            
            // If Type is a string, try to convert it
            if (typeof type === 'string') {
                // Check if the string is a valid key in GmtOffsetTypes
                const typeKey = type as keyof typeof GmtOffsetTypes
                if (typeKey in GmtOffsetTypes) {
                    // Convert string to number using the enum
                    const newValue = GmtOffsetTypes[typeKey]

                    dashboard.DashboardConf.GMTOffsetData.Type = newValue
                } else {
                    // If it's not a valid key, remove Type from payload
                    // Backend will use the existing value from DB
                    delete dashboard.DashboardConf.GMTOffsetData.Type
                }
            }
        }
        
        return dashboard
    },

    /**
     * Bulk update multiple dashboards at once
     * Used primarily for reordering dashboards
     */
    async bulkUpdate (dashboards: Array<Partial<IDashboard>>) {
        try {
            const dashboardsToUpdate = dashboards.map(dashboard => {
                const copyOfData = cloneDeep(dashboard)
                if (copyOfData.WidgetGroupList) {
                    delete copyOfData.WidgetGroupList
                }
                // Normalize GMTOffsetData.Type to ensure it's always an integer
                return this.normalizeGMTOffsetType(copyOfData)
            })

            const response = await $axios.post('/DashBoards/BulkUpdate/', {
                Dashboards: dashboardsToUpdate
            })
            return response
        } catch (e) {
            parseCatch(e, true, 'Bulk Update Dashboards')
        }
    }
}
